// =============================================================================
// ui/shell/FilterChips.tsx — the always-visible active-filter state.
// -----------------------------------------------------------------------------
// ⚠ THE POINT IS THAT IT IS NOT IN THE DRAWER. Every active filter becomes a
// labelled, removable chip in the toolbar, so a filter left on somewhere the
// user is not looking cannot silently scope every KPI on every tab. The old
// client says the same thing in its own comment: "a stuck global filter can
// never silently skew every KPI".
//
// The date range is deliberately NOT chipped. It is always set to something,
// always visible on the Filters button, and chipping it would mean a permanent
// chip that reads as an anomaly rather than as the normal state.
//
// ── ⚠ CHIPS COME FROM THE APPLIED SELECTION, NOT THE STORED ONE ──
//
// `useActiveFacets()`, never `useFilters().storedFacets`. The two differ exactly
// when an id outlived its registry, and a chip for a filter that is not being
// applied is as misleading as no chip for one that is — it names a constraint
// the numbers were never scoped by.
// =============================================================================

import { useMemo } from 'react';
import { useAccounts } from '../state/accounts';
import { usePatterns } from '../state/patterns';
import { usePreferences } from '../state/preferences';
import { useActiveFacets, useFilters, UNASSIGNED_FILTER_ID, type FacetDim } from '../state/filters';
import { ANNOTATION_OPTS, parseTagRegistry } from '../../selectors/accounts';

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface Chip {
  key: string;
  kind: string;
  label: string;
  color?: string | undefined;
  remove: () => void;
}

export function FilterChips() {
  const f = useFilters();
  const { facets } = useActiveFacets();
  const { accounts } = useAccounts();
  const { patterns } = usePatterns();
  const { prefs } = usePreferences();

  // Same parser as the drawer and the trade form — see `parseTagRegistry`.
  const registry = useMemo(() => parseTagRegistry(prefs?.customTags), [prefs]);

  const chips: Chip[] = [];

  for (const id of f.accounts) {
    const label = id === UNASSIGNED_FILTER_ID
      ? 'Unassigned'
      : (accounts ?? []).find((a) => String(a.id) === id)?.name || 'Account';
    chips.push({ key: `a:${id}`, kind: 'Account', label, remove: () => f.toggleAccount(id) });
  }

  // ⚠ THE FACET ORDER IS BETA'S `facetFilterChips()`: instrument, tag, pattern,
  // day, annotation. Chips are read left to right beside the toolbar, so the
  // order is part of what the bar says.
  for (const sym of facets.instruments) {
    chips.push({
      key: `i:${sym}`, kind: 'Instrument', label: sym,
      remove: () => f.toggleFacet('instruments' as FacetDim, sym),
    });
  }

  // ⚠ Carries the user's own COLOUR, as beta does — the swatch is how a tag is
  // recognised at a glance, and the drawer shows the same one.
  for (const id of facets.tags) {
    const tag = registry.find((t) => t.id === String(id));
    chips.push({
      key: `t:${id}`, kind: 'Tag', label: label(prefs, tag?.name, 'tag'),
      color: tag?.color || undefined,
      remove: () => f.toggleFacet('tags' as FacetDim, id),
    });
  }

  // ⚠ A DIMENSION WITHOUT A CHIP IS AN INVISIBLE FILTER. The Pattern facet
  // shipped one commit before this loop existed, and for that commit the badge on
  // the Filters button read "1" while nothing on the always-visible bar said
  // which pattern — which is precisely what this component's header forbids
  // ("a stuck filter must never be invisible"). Found by ticking one on the live
  // page and looking at the bar, not by any test.
  for (const id of facets.patterns) {
    const name = (patterns ?? []).find((p) => String(p.id) === String(id))?.name;
    chips.push({
      key: `p:${id}`, kind: 'Pattern', label: label(patterns, name, 'pattern'),
      remove: () => f.toggleFacet('patterns' as FacetDim, id),
    });
  }

  for (const d of facets.daysOfWeek) {
    chips.push({
      key: `d:${d}`, kind: 'Day', label: DOW_LABELS[d] ?? String(d),
      remove: () => f.toggleFacet('daysOfWeek' as FacetDim, d),
    });
  }

  // ⚠ Labelled from the same list the drawer offers, so the chip reads back the
  // row that was ticked. The vocabulary is fixed and validated at the storage
  // parse, so there is no unresolved case here.
  for (const v of facets.annotation) {
    const o = ANNOTATION_OPTS.find((x) => x.value === v);
    if (!o) continue;
    chips.push({
      key: `n:${v}`, kind: 'Annotation', label: o.label,
      remove: () => f.toggleFacet('annotation' as FacetDim, v),
    });
  }

  if (!chips.length) return null;

  return (
    <div className="filter-chips" data-testid="filter-chips">
      <span className="filter-chip-lead">Filtered</span>
      {chips.map((c) => (
        <button
          key={c.key}
          type="button"
          className="filter-chip"
          title={`${c.kind}: ${c.label} — click to remove`}
          onClick={c.remove}
        >
          {c.color
            ? <span className="filter-chip-dot" style={{ background: c.color }} aria-hidden="true" />
            : null}
          <span className="filter-chip-k">{c.kind}</span>
          {c.label}
          <span aria-hidden="true"> ×</span>
        </button>
      ))}
      <button type="button" className="filter-clear-all" onClick={f.clearAll}>Clear all</button>
    </div>
  );
}

/**
 * A chip label for an id that names something in a registry.
 *
 * ── ⚠ "STILL LOADING" AND "DELETED" ARE DIFFERENT, AND CONFLATING THEM SHOWED
 * A LIE ON EVERY REFRESH ──
 *
 * The first version read `find(...)?.name ?? '(deleted pattern)'`. While the
 * catalogue was still loading that `find` runs over an empty list, so a
 * perfectly live pattern chipped as **(deleted pattern)** on every single page
 * load that had one selected — brief, but wrong, and it is the kind of flash a
 * user reads as data loss.
 *
 * `null` registry means UNKNOWN, so the chip says nothing it cannot support. A
 * loaded registry that still does not resolve the id cannot normally happen —
 * `useActiveFacets` prunes those before they get here — so the deleted label is
 * a backstop rather than the expected path.
 */
function label(registry: unknown, name: string | undefined, kind: string): string {
  if (name) return name;
  return registry == null ? '…' : `(deleted ${kind})`;
}
